/*
 * ICONS COME FROM @mui/icons-material, NOT @ant-design/icons.
 *
 * The rest of this portal is Ant Design, and these are the deliberate
 * exception: the sidebar is meant to be the same furniture as the internal
 * admin console (see the note at the top of components/layout/AppLayout.jsx),
 * and the two icon sets draw the same concepts differently — antd's
 * DashboardOutlined is a gauge, MUI's DashboardIcon is a four-square grid. No
 * antd icon is a match for the admin glyph, only an approximation, so the
 * glyphs themselves are imported from admin's set and each item below uses the
 * icon admin uses for the same thing.
 *
 * Sizing is applied by AppLayout, not here: MUI icons do not inherit font-size
 * the way antd's do, so the layout clones each element with the size the slot
 * needs — 22 for a top-level item, 20 for a nested one, matching admin.
 *
 * ── IMPORT THE PATH ADMIN IMPORTS, NOT THE NAME IT BINDS ──
 * Admin's icon variables are not named after their modules. `DashboardIcon`
 * there is `@mui/icons-material/DashboardOutlined`, and the same is true of
 * `TrendingUpIcon`, `LocalShippingIcon`, `MonetizationOnIcon` and `FlightIcon`
 * — every one of them an Outlined variant bound to an un-suffixed name. Reading
 * the names and importing `Dashboard`, `TrendingUp` and so on pulls the FILLED
 * glyphs, which carry far more ink at the same 22px box and read as larger and
 * heavier than admin's. Check the module path in admin/config/navigation.js
 * before adding an icon here.
 */
import DashboardIcon from '@mui/icons-material/DashboardOutlined';
import ExploreOutlinedIcon from '@mui/icons-material/ExploreOutlined';
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUpOutlined';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import LocalShippingIcon from '@mui/icons-material/LocalShippingOutlined';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOnOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import PercentOutlinedIcon from '@mui/icons-material/PercentOutlined';

/**
 * Section-grouped sidebar navigation for the Partner Portal.
 * Groups with `collapsible: true` render as expandable parent items with children.
 * Groups without it render as flat section labels with items underneath.
 *
 * `apiStatus` puts a QA-only dot on an item's icon — 'in_progress' (yellow) once
 * the screen's spec endpoints are wired, 'verified' (green) once QA signs off.
 * Gated by SHOW_API_STATUS_BADGE in flagSwitch/SwitchConfig; see
 * constants/apiStatusBadge.js.
 */
const sidebarNav = [
  {
    section: 'Overview',
    items: [
      { key: 'dashboard', label: 'Dashboard', icon: <DashboardIcon />, path: '/partnerAdmin', apiStatus: 'verified' },
    ],
  },
  {
    section: 'Travel',
    collapsible: true,
    icon: <ExploreOutlinedIcon />,
    items: [
      /**
       * SMB-4110 — `newChat` puts a refresh glyph on this row that starts a
       * FRESH conversation, as distinct from navigating to the chat surface.
       * Clicking the row while already on Start Chat does nothing: the
       * conversation lives in the embedded traveller app, and `TravelAppFrame`
       * deliberately skips its `zenvoya:set-surface` message when the frame
       * already shows the surface being asked for. `SidebarItem` renders the
       * glyph; see `components/travelApp/newChatSignal.js` for the mechanism.
       */
      { key: 'travel-chat', label: 'Start Chat', icon: <ChatOutlinedIcon />, path: '/partnerAdmin/travel/chat', newChat: true },
      { key: 'travel-shop', label: 'Book', icon: <ShoppingBagOutlinedIcon />, path: '/partnerAdmin/travel/shop' },
      { key: 'travel-history', label: 'Chat History', icon: <HistoryOutlinedIcon />, path: '/partnerAdmin/travel/history' },
    ],
  },
  {
    section: 'Bookings',
    collapsible: true,
    icon: <ConfirmationNumberOutlinedIcon />,
    items: [
      { key: 'booking-management', label: 'Bookings', icon: <ConfirmationNumberOutlinedIcon />, path: '/partnerAdmin/bookings', apiStatus: 'verified'},
      { key: 'servicing', label: 'Requests', icon: <AssignmentOutlinedIcon />, path: '/partnerAdmin/servicing', apiStatus: 'in_progress' },
      // Hidden from the sidebar for now; the routes stay live so the pages are
      // still reachable directly at /partnerAdmin/bookings/manual-hotel|manual-flight.
      // { key: 'manual-hotel-booking', label: 'Hotel Manual Form', icon: <ConfirmationNumberOutlinedIcon />, path: '/partnerAdmin/bookings/manual-hotel', apiStatus: 'in_progress' },
      // { key: 'manual-flight-booking', label: 'Flight Manual Form', icon: <ConfirmationNumberOutlinedIcon />, path: '/partnerAdmin/bookings/manual-flight', apiStatus: 'in_progress' },
      { key: 'communications', label: 'Communications', icon: <ForumOutlinedIcon />, path: '/partnerAdmin/communications', apiStatus: 'verified' },
      { key: 'customers', label: 'Customers', icon: <PersonOutlinedIcon />, path: '/partnerAdmin/customers', apiStatus: 'verified' },
    ],
  },
  {
    section: 'Reports',
    collapsible: true,
    icon: <AssessmentOutlinedIcon />,
    items: [
      { key: 'sales-report', label: 'Sales', icon: <TrendingUpIcon />, path: '/partnerAdmin/reports/sales', apiStatus: 'in_progress' },
      { key: 'agent-report', label: 'Agents', icon: <PeopleOutlinedIcon />, path: '/partnerAdmin/reports/agents', apiStatus: 'in_progress' },
      { key: 'supplier-report', label: 'Suppliers', icon: <LocalShippingIcon />, path: '/partnerAdmin/reports/suppliers', apiStatus: 'in_progress' },
      { key: 'transaction-report', label: 'Transactions', icon: <MonetizationOnIcon />, path: '/partnerAdmin/reports/transactions', apiStatus: 'in_progress' },
    ],
  },
  {
    section: 'Settings',
    collapsible: true,
    icon: <SettingsOutlinedIcon />,
    items: [
      { key: 'markup', label: 'Markup & Fees', icon: <TrendingUpIcon />, path: '/partnerAdmin/commercials/markup', apiStatus: 'verified' },
      { key: 'users', label: 'Users & Roles', icon: <PeopleOutlinedIcon />, path: '/partnerAdmin/platform/users', apiStatus: 'verified' },
      { key: 'branding', label: 'Branding', icon: <PaletteOutlinedIcon />, path: '/partnerAdmin/branding', apiStatus: 'verified' },
      { key: 'finance', label: 'Billing', icon: <AccountBalanceWalletOutlinedIcon />, path: '/partnerAdmin/finance/transactions', apiStatus: 'verified' },
      // Live since 2026-10-08 (registry.json partnerGstRatesView /
      // partnerGstRatesUpdate are live:true). Flip to 'verified' once
      // smoke-tested on the stage.
      { key: 'gst', label: 'GST', icon: <PercentOutlinedIcon />, path: '/partnerAdmin/gst', apiStatus: 'in_progress' },
      { key: 'promo-codes', label: 'Promotions', icon: <LocalOfferOutlinedIcon />, path: '/partnerAdmin/commercials/promo-codes', apiStatus: 'verified' },
      // The old mock screen, kept visible for side-by-side comparison with the
      // wired Promo Codes page above. Remove this item once it is no longer needed.
      // { key: 'promos-old', label: 'Promos & Vouchers', icon: <LocalOfferOutlinedIcon />, path: '/partnerAdmin/commercials/promos' },
    ],
  },
];

export default sidebarNav;
